/**
 * JWT Decode Utility for Local Development
 *
 * Decodes Firebase JWT tokens client-side to extract the user ID.
 *
 * IMPORTANT: This does NOT validate the token - it only decodes the payload.
 * In production, EnvoyGateway validates the JWT and extracts the user ID.
 * This utility is only used for local backend mode when EnvoyGateway is not available.
 */

import { jwtDecode, type JwtPayload } from "jwt-decode";

interface FirebaseJwtPayload extends JwtPayload {
  /** Alternative user ID claim used by Firebase */
  user_id?: string;
}

/**
 * Decode a Firebase JWT token to extract the user ID.
 *
 * @param token - The Firebase JWT token string
 * @returns The user ID, or null if decoding fails
 */
export function decodeFirebaseJwt(token: string): { uid: string } | null {
  try {
    const payload = jwtDecode<FirebaseJwtPayload>(token);

    // Firebase uses 'sub' or 'user_id' for the user ID
    const uid = payload.sub || payload.user_id;
    if (!uid) {
      console.warn("No user ID found in JWT payload");
      return null;
    }

    return { uid };
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}
